import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";

import { db } from "../firebase";
import LeagueTabs from "../pages/LeagueTabs";

import "./Sports.css";

const TIMEZONE = "Africa/Dar_es_Salaam";
const MATCHES_COLLECTION = "matches";

const BASE_COUNTS = {
  all: 985,
};

const FIFTEEN_MINUTES = 15 * 60 * 1000;
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const INITIAL_VISIBLE = 30;

/* =========================================================
   LOCAL STORAGE
========================================================= */

const MATCHES_STORAGE_KEY = "sports_matches_cache_v1";

/* =========================================================
   DATE PARSER
========================================================= */

const parseDate = (date, time = "") => {
  if (!date) {
    return 0;
  }

  try {
    if (
      typeof date === "object" &&
      typeof date.toDate === "function"
    ) {
      const value = date.toDate().getTime();

      return Number.isNaN(value) ? 0 : value;
    }

    if (date instanceof Date) {
      const value = date.getTime();

      return Number.isNaN(value) ? 0 : value;
    }

    if (
      typeof date === "object" &&
      typeof date.seconds === "number"
    ) {
      const value = Number(date.seconds) * 1000;

      return Number.isNaN(value) ? 0 : value;
    }

    const dateString = String(date).trim();

    if (!dateString) {
      return 0;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      let cleanTime = String(time || "00:00").trim();

      if (/^\d{1,2}:\d{2}$/.test(cleanTime)) {
        const [hour, minute] = cleanTime.split(":");

        cleanTime = `${String(Number(hour)).padStart(
          2,
          "0"
        )}:${minute}`;
      } else {
        cleanTime = "00:00";
      }

      const isoDate = `${dateString}T${cleanTime}:00+03:00`;
      const value = new Date(isoDate).getTime();

      return Number.isNaN(value) ? 0 : value;
    }

    const cleanDate = dateString.replace(" ", "T");
    const value = new Date(cleanDate).getTime();

    return Number.isNaN(value) ? 0 : value;
  } catch (error) {
    console.error("DATE PARSE ERROR:", error);
    return 0;
  }
};

/* =========================================================
   FILTER AND SORT MATCHES
========================================================= */

const filterAndSortMatches = (
  rawMatches,
  currentTime = Date.now()
) => {
  if (!Array.isArray(rawMatches)) {
    return [];
  }

  const data = rawMatches.filter((match) => {
    const matchTime = parseDate(
      match.date,
      match.time
    );

    if (!matchTime) {
      return false;
    }

    const timeLeft = matchTime - currentTime;

    return (
      timeLeft > FIFTEEN_MINUTES &&
      matchTime < currentTime + THIRTY_DAYS
    );
  });

  data.sort((a, b) => {
    const timeA = parseDate(a.date, a.time);
    const timeB = parseDate(b.date, b.time);

    return timeA - timeB;
  });

  return data;
};

/* =========================================================
   LOCAL STORAGE HELPERS
========================================================= */

const readMatchesFromStorage = () => {
  try {
    if (typeof window === "undefined") {
      return [];
    }

    const savedMatches = window.localStorage.getItem(
      MATCHES_STORAGE_KEY
    );

    if (!savedMatches) {
      return [];
    }

    const parsedMatches = JSON.parse(savedMatches);

    return filterAndSortMatches(parsedMatches);
  } catch (error) {
    console.error(
      "READ MATCHES CACHE ERROR:",
      error
    );

    return [];
  }
};

const saveMatchesToStorage = (matches) => {
  try {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      MATCHES_STORAGE_KEY,
      JSON.stringify(matches)
    );
  } catch (error) {
    console.error(
      "SAVE MATCHES CACHE ERROR:",
      error
    );
  }
};

/* =========================================================
   TANZANIA DATE PARTS
========================================================= */

const getTanzaniaDateParts = (timestamp) => {
  if (!timestamp) {
    return {
      year: "",
      month: "",
      day: "",
    };
  }

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(timestamp));

  return {
    year:
      parts.find((part) => part.type === "year")?.value || "",

    month:
      parts.find((part) => part.type === "month")?.value || "",

    day:
      parts.find((part) => part.type === "day")?.value || "",
  };
};

/* =========================================================
   TODAY
========================================================= */

const getTodayString = () => {
  const parts = getTanzaniaDateParts(Date.now());

  return `${parts.year}-${parts.month}-${parts.day}`;
};

/* =========================================================
   TOMORROW
========================================================= */

const getTomorrowString = () => {
  const today = getTodayString();

  const [year, month, day] = today.split("-").map(Number);

  const tomorrow = new Date(
    Date.UTC(year, month - 1, day + 1, 12, 0, 0)
  );

  return tomorrow.toISOString().slice(0, 10);
};

/* =========================================================
   MATCH DATE STRING
========================================================= */

const getMatchDateString = (match) => {
  const timestamp = parseDate(
    match.date,
    match.time
  );

  if (!timestamp) {
    return "";
  }

  const parts = getTanzaniaDateParts(timestamp);

  return `${parts.year}-${parts.month}-${parts.day}`;
};

/* =========================================================
   DATE HEADER
========================================================= */

const formatDateHeader = (dateString) => {
  if (!dateString) {
    return "";
  }

  const timestamp = new Date(
    `${dateString}T12:00:00+03:00`
  ).getTime();

  if (Number.isNaN(timestamp)) {
    return dateString;
  }

  return new Date(timestamp).toLocaleDateString("en-GB", {
    timeZone: TIMEZONE,
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* =========================================================
   COMPONENT
========================================================= */

export default function Sports() {
  const navigate = useNavigate();

  const [matches, setMatches] = useState(() =>
    readMatchesFromStorage()
  );

  const [visible, setVisible] = useState(
    INITIAL_VISIBLE
  );

  const [tab, setTab] = useState("next");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(() => {
    return readMatchesFromStorage().length === 0;
  });

  const [dayFilter, setDayFilter] = useState("today");
  const [now, setNow] = useState(Date.now());

  /* =======================================================
     LIVE CLOCK
  ======================================================= */

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  /* =======================================================
     LOAD MATCHES FROM FIREBASE + LOCAL STORAGE
  ======================================================= */

  useEffect(() => {
    let cancelled = false;

    const loadMatches = async () => {
      try {
        /*
          Kama cache ipo, ionyeshe kwanza.
          Firebase itaendelea kufetch bila kuzuia cache.
        */
        if (!cancelled) {
          const cachedMatches = readMatchesFromStorage();

          if (cachedMatches.length > 0) {
            setMatches(cachedMatches);
            setLoading(false);
          } else {
            setLoading(true);
          }
        }

        const snapshot = await getDocs(
          collection(db, MATCHES_COLLECTION)
        );

        if (cancelled) {
          return;
        }

        const currentTime = Date.now();

        const firebaseMatches = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        /*
          Sheria zote za zamani zinatumika hapa:
          - date lazima iwe valid
          - match iwe zaidi ya dakika 15 kutoka sasa
          - match isiwe zaidi ya siku 30
          - sorting kwa muda wa match
        */
        const data = filterAndSortMatches(
          firebaseMatches,
          currentTime
        );

        /*
          Hapa match iliyobadilishwa Firebase
          itaingia kwenye state na localStorage.
        */
        setMatches(data);
        saveMatchesToStorage(data);
      } catch (error) {
        console.error(
          "FIRESTORE MATCHES ERROR:",
          error
        );

        /*
          Firebase ikifeli, tumia cache iliyopo.
        */
        if (!cancelled) {
          const cachedMatches = readMatchesFromStorage();

          setMatches(cachedMatches);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMatches();

    const refreshTimer = setInterval(() => {
      loadMatches();
    }, 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(refreshTimer);
    };
  }, []);

  /* =======================================================
     FUTURE MATCHES
  ======================================================= */

  const liveMatches = useMemo(() => {
    return matches
      .filter((match) => {
        const matchTime = parseDate(
          match.date,
          match.time
        );

        return matchTime > now + FIFTEEN_MINUTES;
      })
      .sort((a, b) => {
        const timeA = parseDate(a.date, a.time);
        const timeB = parseDate(b.date, b.time);

        return timeA - timeB;
      });
  }, [matches, now]);

  const allMatches = liveMatches;
  const next = liveMatches;
  const featured = liveMatches.slice(0, 50);

  /* =======================================================
     SEARCH AND FILTER
  ======================================================= */

  const showMatches = useMemo(() => {
    let result = [];

    if (tab === "all") {
      result = [...allMatches];
    } else if (tab === "featured") {
      result = [...featured];
    } else {
      result = [...next];
    }

    if (search.trim()) {
      const query = search.trim().toLowerCase();

      result = result.filter((match) => {
        const teamA = String(
          match.A ||
            match.teamA ||
            match.homeTeam ||
            ""
        ).toLowerCase();

        const teamB = String(
          match.B ||
            match.teamB ||
            match.awayTeam ||
            ""
        ).toLowerCase();

        const league = String(
          match.league ||
            match.competition ||
            ""
        ).toLowerCase();

        const country = String(
          match.country ||
            match.countryName ||
            match.nation ||
            match.region ||
            ""
        ).toLowerCase();

        return (
          teamA.includes(query) ||
          teamB.includes(query) ||
          league.includes(query) ||
          country.includes(query)
        );
      });
    }

    if (dayFilter !== "all") {
      const targetDate =
        dayFilter === "today"
          ? getTodayString()
          : getTomorrowString();

      result = result.filter((match) => {
        return getMatchDateString(match) === targetDate;
      });
    }

    result.sort((a, b) => {
      const timeA = parseDate(a.date, a.time);
      const timeB = parseDate(b.date, b.time);

      return timeA - timeB;
    });

    return result;
  }, [
    tab,
    search,
    dayFilter,
    allMatches,
    featured,
    next,
  ]);

  /* =======================================================
     GROUP BY DATE AND LEAGUE
  ======================================================= */

  const grouped = useMemo(() => {
    const dateGroups = {};

    showMatches.forEach((match) => {
      const timestamp = parseDate(
        match.date,
        match.time
      );

      if (!timestamp) {
        return;
      }

      const dateKey = getMatchDateString(match);

      if (!dateKey) {
        return;
      }

      const league =
        match.league ||
        match.competition ||
        "Other";

      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = {};
      }

      if (!dateGroups[dateKey][league]) {
        dateGroups[dateKey][league] = [];
      }

      dateGroups[dateKey][league].push(match);
    });

    const sortedDates = Object.keys(dateGroups).sort(
      (a, b) => {
        const dateA = new Date(
          `${a}T12:00:00+03:00`
        ).getTime();

        const dateB = new Date(
          `${b}T12:00:00+03:00`
        ).getTime();

        return dateA - dateB;
      }
    );

    sortedDates.forEach((dateKey) => {
      Object.keys(dateGroups[dateKey]).forEach(
        (league) => {
          dateGroups[dateKey][league].sort((a, b) => {
            const timeA = parseDate(a.date, a.time);
            const timeB = parseDate(b.date, b.time);

            return timeA - timeB;
          });
        }
      );
    });

    return sortedDates.map((dateKey) => ({
      dateKey,
      leagues: dateGroups[dateKey],
    }));
  }, [showMatches]);

  /* =======================================================
     RESET VISIBLE COUNT
  ======================================================= */

  useEffect(() => {
    setVisible(INITIAL_VISIBLE);
  }, [tab, search, dayFilter]);

  /* =======================================================
     FORMAT MATCH TIME
  ======================================================= */

  const formatTime = (date, time) => {
    const timestamp = parseDate(date, time);

    if (!timestamp) {
      return "-";
    }

    return new Date(timestamp).toLocaleString("en-GB", {
      timeZone: TIMEZONE,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* =======================================================
     RENDER MATCH
  ======================================================= */

  const renderMatch = (match, index) => {
    const teamA =
      match.A ||
      match.teamA ||
      match.homeTeam ||
      "Team A";

    const teamB =
      match.B ||
      match.teamB ||
      match.awayTeam ||
      "Team B";

    const league =
      match.league ||
      match.competition ||
      "League";

    const country =
      match.country ||
      match.countryName ||
      match.nation ||
      match.region ||
      "Unknown";

    const matchTime = parseDate(
      match.date,
      match.time
    );

    const matchKey =
      match.id ||
      `${teamA}-${teamB}-${match.date}-${match.time}-${index}`;

    const openMatch = () => {
      if (!match.id) {
        return;
      }

      navigate(`/betscores/${match.id}`, {
        state: match,
      });
    };

    return (
      <div
        className="sports-match-card"
        key={matchKey}
        onClick={openMatch}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" ||
            event.key === " "
          ) {
            event.preventDefault();
            openMatch();
          }
        }}
      >
        {/* LEFT SIDE */}
        <div className="sports-match-info">
          <div className="sports-teams">
            <span className="sports-team-name">
              {teamA}
            </span>

            <span className="sports-vs">
              VS
            </span>

            <span className="sports-team-name">
              {teamB}
            </span>
          </div>

          <div className="sports-match-league">
            {league}
          </div>

          <div className="sports-match-meta">
            <span className="sports-time">
              {matchTime
                ? formatTime(
                    match.date,
                    match.time
                  )
                : "-"}
            </span>
          </div>
        </div>

        {/* RIGHT SIDE: COUNTRY */}
        <div className="sports-match-country">
          <span className="sports-country-icon">
            🌍
          </span>

          <span className="sports-country-name">
            {country}
          </span>
        </div>
      </div>
    );
  };

  /* =======================================================
     LIMIT VISIBLE MATCHES
  ======================================================= */

  let renderedCount = 0;
  const visibleGrouped = [];

  for (const dateGroup of grouped) {
    if (renderedCount >= visible) {
      break;
    }

    const dateLeagues = [];

    for (const [
      league,
      leagueMatches,
    ] of Object.entries(dateGroup.leagues)) {
      if (renderedCount >= visible) {
        break;
      }

      const remaining = visible - renderedCount;

      const matchesToShow = leagueMatches.slice(
        0,
        remaining
      );

      if (matchesToShow.length > 0) {
        dateLeagues.push({
          league,
          matches: matchesToShow,
        });

        renderedCount += matchesToShow.length;
      }
    }

    if (dateLeagues.length > 0) {
      visibleGrouped.push({
        dateKey: dateGroup.dateKey,
        leagues: dateLeagues,
      });
    }
  }

  const canViewOthers =
    visible < showMatches.length;

  const handleViewOthers = () => {
    setVisible(showMatches.length);
  };

  /* =======================================================
     MAIN UI
  ======================================================= */

  return (
    <main className="sports-page">
      <div className="sports-page-inner">
        {/* LEAGUE TABS */}
        <section className="sports-league-tabs">
          <LeagueTabs />
        </section>

        {/* CONTROLS */}
        <section className="sports-controls">
          <div className="sports-select">
            <button
              type="button"
              className={
                tab === "next"
                  ? "sports-select-box active"
                  : "sports-select-box"
              }
              onClick={() => {
                setTab("next");
                setVisible(INITIAL_VISIBLE);
              }}
            >
              <span className="label">
                Next
              </span>
            </button>

            <button
              type="button"
              className={
                tab === "featured"
                  ? "sports-select-box active"
                  : "sports-select-box"
              }
              onClick={() => {
                setTab("featured");
                setVisible(INITIAL_VISIBLE);
              }}
            >
              <span className="label">
                Featured
              </span>
            </button>

            <button
              type="button"
              className={
                tab === "all"
                  ? "sports-select-box active"
                  : "sports-select-box"
              }
              onClick={() => {
                setTab("all");
                setVisible(INITIAL_VISIBLE);
              }}
            >
              <span className="count">
                {BASE_COUNTS.all + allMatches.length}
              </span>

              <span className="label">
                All
              </span>
            </button>
          </div>

          {/* SEARCH */}
          <div className="sports-search-wrap">
            <span className="sports-search-icon">
              🔎
            </span>

            <input
              type="text"
              placeholder="Search team or league..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setVisible(INITIAL_VISIBLE);
              }}
              className="sports-search-input"
            />
          </div>

          {/* DAY FILTER */}
          <div className="sports-day-tabs">
            <button
              type="button"
              className={
                dayFilter === "today" ? "active" : ""
              }
              onClick={() => {
                setDayFilter("today");
                setVisible(INITIAL_VISIBLE);
              }}
            >
              Today
            </button>

            <button
              type="button"
              className={
                dayFilter === "tomorrow"
                  ? "active"
                  : ""
              }
              onClick={() => {
                setDayFilter("tomorrow");
                setVisible(INITIAL_VISIBLE);
              }}
            >
              Tomorrow
            </button>

            <button
              type="button"
              className={
                dayFilter === "all" ? "active" : ""
              }
              onClick={() => {
                setDayFilter("all");
                setVisible(INITIAL_VISIBLE);
              }}
            >
              All
            </button>
          </div>
        </section>

        {/* LOADING */}
        {loading && (
          <div className="sports-loading-overlay">
            <div className="sports-loading-box">
              <div className="sports-loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* MATCHES */}
        {!loading && (
          <section className="sports-matches-container">
            {visibleGrouped.map(
              ({ dateKey, leagues }) => (
                <div
                  className="sports-date-section"
                  key={`date-${dateKey}`}
                >
                  <div className="sports-date-title">
                    <span className="sports-date-dot"></span>

                    <span>
                      {formatDateHeader(dateKey)}
                    </span>
                  </div>

                  {leagues.map(
                    ({ league, matches: leagueMatches }) => (
                      <div
                        className="sports-league-section"
                        key={`league-${dateKey}-${league}`}
                      >
                        <div className="sports-league-title">
                          <span className="sports-league-dot"></span>

                          <span>
                            {league}
                          </span>
                        </div>

                        {leagueMatches.map(renderMatch)}
                      </div>
                    )
                  )}
                </div>
              )
            )}
          </section>
        )}

        {/* VIEW OTHERS */}
        {!loading && canViewOthers && (
          <button
            type="button"
            className="sports-more-btn"
            onClick={handleViewOthers}
          >
            View Others
          </button>
        )}

        {/* EMPTY */}
        {!loading && showMatches.length === 0 && (
          <div className="sports-empty-state">
            <div className="sports-empty-icon">
              ⚽
            </div>

            <strong>
              No Matches Found
            </strong>

            <small>
              Try another team, league or day.
            </small>
          </div>
        )}
      </div>
    </main>
  );
}